import { useId, useState, type ReactNode } from 'react';

import type { ChartData } from '../types';
import { DataTable } from './templates/table';

/**
 * The card a chart lives in: an explanatory (i) top-left, the title, and the
 * toggle between the chart and its table.
 *
 * The (i) sits at the top-left of every chart (as it did in Superset). On hover
 * or keyboard focus it shows what the chart is measuring; a click pins it open
 * for touch, where there is no hover. Its content is the chart's `description`.
 *
 * The table twin is not a nicety. It is what makes the rest of the chart layer
 * legal — a value pushed off a small bar into a tooltip, a series colour that
 * sits below 3:1 on the surface, a rounded headline figure. Each of those is
 * fine *because* the exact numbers are one click away and reachable by keyboard
 * and screen reader. Remove the toggle and those decisions stop being defensible.
 */

interface Props {
  title?: string;
  /** Shown in the (i) tooltip — what the chart measures, and any caveat. */
  description?: string;
  data?: ChartData;
  /** Widget embeds are one chart in a small card; the platform draws the header. */
  chrome?: boolean;
  footnote?: string;
  /**
   * Author CSS that restyles this card. It is nested under a per-chart scope
   * class at render time, so it can only ever affect this chart — never leak to
   * the rest of the page. `scopeId` makes the scope unique (the chart's id).
   */
  css?: string;
  scopeId?: string;
  children: ReactNode;
}

export const ChartFrame = ({
  title,
  description,
  data,
  chrome = true,
  footnote,
  css,
  scopeId,
  children,
}: Props) => {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const infoId = useId();
  // Pins the tooltip open on tap — touch devices have no hover state.
  const [pinned, setPinned] = useState(false);

  const canTable = Boolean(data?.rows.length);

  // A truncated result must say so, or a table of the first 5,000 of 95,000
  // rows reads as the complete answer.
  const truncation =
    data?.totalRows && data.totalRows > data.rows.length
      ? `Showing the first ${data.rows.length.toLocaleString('en-GB')} of ${data.totalRows.toLocaleString('en-GB')} rows.`
      : null;
  const note = [footnote, truncation].filter(Boolean).join(' ');

  // Every chart gets an (i); when no description is set it nudges the author to
  // add one rather than showing an empty bubble.
  const info =
    description?.trim() ||
    'No description yet — add one in the studio so readers know what this chart shows.';

  // Author CSS, scoped to this chart's own card via native CSS nesting, so a
  // selector like `.chart-card__title` only ever restyles THIS chart. The
  // `</style` and `@import` strips are belt-and-braces: the former cannot break
  // out of the tag anyway (React sets it as text), the latter blocks pulling in
  // an external sheet.
  const scopeClass = scopeId ? `chart-scope-${scopeId}` : undefined;
  const scopedCss =
    css?.trim() && scopeClass
      ? `.${scopeClass}{${css.replace(/<\/style/gi, '').replace(/@import[^;]*;?/gi, '')}}`
      : null;

  const card = (
    <figure className="chart-card">
      {chrome && (
        <figcaption className="chart-card__head">
          <div className="chart-card__lead">
            <span
              className={`chart-card__info-wrap${pinned ? ' is-pinned' : ''}`}
              onMouseLeave={() => setPinned(false)}
            >
              <button
                type="button"
                className="icon-button chart-card__info-btn"
                aria-describedby={infoId}
                aria-expanded={pinned}
                aria-label={`About ${title ?? 'this chart'}`}
                onClick={() => setPinned((open) => !open)}
              >
                i
              </button>
              <span role="tooltip" id={infoId} className="chart-card__tip">
                {info}
              </span>
            </span>
            {title && <h4 className="chart-card__title">{title}</h4>}
          </div>

          {canTable && (
            <div className="chart-card__tools">
              <button
                type="button"
                className="ghost chart-card__toggle"
                onClick={() => setView((v) => (v === 'chart' ? 'table' : 'chart'))}
              >
                {view === 'chart' ? 'Table' : 'Chart'}
              </button>
            </div>
          )}
        </figcaption>
      )}

      <div className="chart-card__body">
        {view === 'table' && data ? <DataTable data={data} /> : children}
      </div>

      {note && <p className="chart-card__footnote">{note}</p>}
    </figure>
  );

  // No scope class → the plain card. Otherwise wrap in a `display:contents`
  // element (which generates no box, so layout is untouched) that carries the
  // scope class; the author CSS is nested under it, so every selector — the
  // card itself included — resolves to a descendant of this chart alone.
  if (!scopeClass) return card;
  return (
    <div className={scopeClass} style={{ display: 'contents' }}>
      {scopedCss && <style>{scopedCss}</style>}
      {card}
    </div>
  );
};
